/* ============================================================================
   KOTT Lab — Projects · Fluid Gallery — production module
   ----------------------------------------------------------------------------
   Full-bleed WebGL gallery: project photos are textures on one plane; scroll
   morphs the current image into the next with a noise displacement + dissolve
   + scroll-velocity chromatic shear, while the cursor drags a liquid lens.
   Kinetic type cross-dissolves with the morph; a ticker + custom "VIEW" cursor
   drive navigation; the whole stage links to the active project.
   Falls back to a CSS cross-fade without WebGL / on a CORS-tainted texture,
   and to a static current project under reduced-motion.
   ============================================================================ */
(function(){
  const root=document.querySelector('.proj-reel');
  if(!root) return;
  const reduced=window.kottReduceMotion?window.kottReduceMotion():matchMedia('(prefers-reduced-motion: reduce)').matches;
  const coarse=matchMedia('(pointer: coarse)').matches;
  const cv=root.querySelector('canvas.gl');
  const wrap=root.querySelector('.wrap');
  const progEl=root.querySelector('.prog');
  const cursor=root.querySelector('.cursor');
  const hit=root.querySelector('.hit');
  const countNow=root.querySelector('.count__now');
  const titles=[...root.querySelectorAll('.titles .t')];
  const fbLayers=[...root.querySelectorAll('.fb__layer')];
  const tks=[...root.querySelectorAll('.ticker .tk')];
  const hintPill=root.querySelector('.hint .scroll-pill');
  const hintChevs=root.querySelector('.hint .hint__chevs');
  const viewLabel=(root.querySelector('.t__cta')&&root.querySelector('.t__cta').textContent.trim())||'';
  const nextLabel=root.getAttribute('data-next-label')||(hintPill?hintPill.textContent:'');
  const rs=root.style;
  if(!cv||!wrap||!titles.length) return;

  const data=titles.map(el=>({
    el,
    img:el.getAttribute('data-img')||'',
    link:el.getAttribute('data-link')||'#',
    accent:(el.getAttribute('data-accent')||'#FF6F3C').trim()
  }));
  const N=data.length, DEN=Math.max(1,N-1);
  const TWO=v=>('0'+v).slice(-2);

  // ---------- WebGL ----------
  let gl=null,glOK=false;
  try{ gl=cv.getContext('webgl',{antialias:false,alpha:false})||cv.getContext('experimental-webgl'); glOK=!!gl; }catch(e){}

  const VS=`attribute vec2 a;void main(){gl_Position=vec4(a,0.,1.);}`;
  const FS=`precision highp float;
    uniform sampler2D t0,t1;uniform vec2 uRes,uR0,uR1,uMouse;
    uniform float uProg,uTime,uVel,uHover;
    float hash(vec2 p){p=fract(p*vec2(123.34,456.21));p+=dot(p,p+45.32);return fract(p.x*p.y);}
    float noise(vec2 p){vec2 i=floor(p),f=fract(p);vec2 u=f*f*(3.-2.*f);
      return mix(mix(hash(i),hash(i+vec2(1.,0.)),u.x),mix(hash(i+vec2(0.,1.)),hash(i+vec2(1.,1.)),u.x),u.y);}
    float fbm(vec2 p){float v=0.,a=.5;for(int i=0;i<5;i++){v+=a*noise(p);p=p*2.03;a*=.5;}return v;}
    vec2 cover(vec2 uv,vec2 tex){vec2 s=uRes/tex;float sc=max(s.x,s.y);vec2 sz=tex*sc;vec2 off=(uRes-sz)*.5;return (uv*uRes-off)/sz;}
    vec3 samp(sampler2D tx,vec2 tr,vec2 uv){return texture2D(tx,clamp(cover(uv,tr),0.0,1.0)).rgb;}
    void main(){
      vec2 uv=gl_FragCoord.xy/uRes;
      float asp=uRes.x/uRes.y;
      // liquid lens following the cursor
      vec2 md=(uv-uMouse)*vec2(asp,1.0);
      float ml=length(md);
      uv += normalize(md+1e-4)/vec2(asp,1.0)*smoothstep(.36,0.,ml)*uHover*0.022*sin(ml*26.-uTime*3.0);
      // flow displacement — peaks mid-transition + with scroll velocity
      float t=uTime*0.05;
      vec2 flow=vec2(fbm(uv*2.6+t),fbm(uv*2.6+vec2(5.1,2.3)-t))-0.5;
      float bulge=sin(clamp(uProg,0.,1.)*3.14159);
      float amp=0.055*bulge+abs(uVel)*0.04;
      vec2 wuv=uv+flow*amp;
      // dissolve threshold from a noise field
      float nf=fbm(uv*3.1+9.0);
      float m=smoothstep(nf-0.32,nf+0.32,uProg);
      // chromatic shear (scroll velocity + transition)
      float ab=(abs(uVel)*0.6+bulge*0.38)*0.011;
      vec2 sh=vec2(ab*sign(uVel+0.0001),ab*0.4);
      vec3 a=vec3(samp(t0,uR0,wuv+sh).r,samp(t0,uR0,wuv).g,samp(t0,uR0,wuv-sh).b);
      vec3 b=vec3(samp(t1,uR1,wuv+sh).r,samp(t1,uR1,wuv).g,samp(t1,uR1,wuv-sh).b);
      vec3 col=mix(a,b,m);
      col*=mix(0.66,1.0,smoothstep(1.25,0.15,length(uv-0.5))); // vignette
      col+=(hash(uv*uRes+t)-0.5)*0.015; // dither
      gl_FragColor=vec4(col,1.0);
    }`;

  let prog,uT,uR,uM,uP,uV,uH,uR0,uR1;
  function sh(ty,src){const s=gl.createShader(ty);gl.shaderSource(s,src);gl.compileShader(s);if(!gl.getShaderParameter(s,gl.COMPILE_STATUS)){console.warn('[proj-fluid]',gl.getShaderInfoLog(s));glOK=false;}return s;}
  if(glOK){try{
    prog=gl.createProgram();gl.attachShader(prog,sh(gl.VERTEX_SHADER,VS));gl.attachShader(prog,sh(gl.FRAGMENT_SHADER,FS));gl.linkProgram(prog);
    if(!gl.getProgramParameter(prog,gl.LINK_STATUS)){console.warn('[proj-fluid] link',gl.getProgramInfoLog(prog));glOK=false;}
    gl.useProgram(prog);
    const b=gl.createBuffer();gl.bindBuffer(gl.ARRAY_BUFFER,b);gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-1,-1,3,-1,-1,3]),gl.STATIC_DRAW);
    const loc=gl.getAttribLocation(prog,'a');gl.enableVertexAttribArray(loc);gl.vertexAttribPointer(loc,2,gl.FLOAT,false,0,0);
    uT=gl.getUniformLocation(prog,'uTime');uR=gl.getUniformLocation(prog,'uRes');uM=gl.getUniformLocation(prog,'uMouse');
    uP=gl.getUniformLocation(prog,'uProg');uV=gl.getUniformLocation(prog,'uVel');uH=gl.getUniformLocation(prog,'uHover');
    uR0=gl.getUniformLocation(prog,'uR0');uR1=gl.getUniformLocation(prog,'uR1');
    gl.uniform1i(gl.getUniformLocation(prog,'t0'),0);gl.uniform1i(gl.getUniformLocation(prog,'t1'),1);
  }catch(e){glOK=false;}}

  function goFallback(){
    glOK=false;root.classList.add('is-nogl');
    // CSS cross-fade is driven in the frame loop via fbLayers
  }
  if(!glOK){goFallback();}

  // ---- textures ----
  const texes=new Array(N).fill(null), texRes=new Array(N).fill([2,2]);
  function makeTex(){const tx=gl.createTexture();gl.bindTexture(gl.TEXTURE_2D,tx);
    gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_S,gl.CLAMP_TO_EDGE);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_T,gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MIN_FILTER,gl.LINEAR);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MAG_FILTER,gl.LINEAR);
    return tx;}
  function accentCanvas(hex){const c=document.createElement('canvas');c.width=c.height=512;const x=c.getContext('2d');
    const g=x.createRadialGradient(360,140,40,256,256,560);g.addColorStop(0,hex);g.addColorStop(1,'#0a0a0f');x.fillStyle=g;x.fillRect(0,0,512,512);return c;}
  function upload(i,src,w,h){
    if(!glOK)return;
    try{
      const tx=makeTex();
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL,true);
      gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA,gl.RGBA,gl.UNSIGNED_BYTE,src);
      texes[i]=tx;texRes[i]=[w,h];
    }catch(e){console.warn('[proj-fluid] texture blocked (CORS?), using fallback',e);goFallback();}
  }
  if(glOK){
    data.forEach((d,i)=>{
      if(d.img){
        const im=new Image();im.crossOrigin='anonymous';
        im.onload=()=>upload(i,im,im.naturalWidth||1600,im.naturalHeight||1000);
        im.onerror=()=>{const c=accentCanvas(d.accent);upload(i,c,512,512);};
        im.src=d.img;
      } else { const c=accentCanvas(d.accent);upload(i,c,512,512); }
    });
  }

  function size(){const d=Math.min(coarse?1.5:2,devicePixelRatio||1);cv.width=Math.round(innerWidth*d);cv.height=Math.round(innerHeight*d);if(glOK)gl.viewport(0,0,cv.width,cv.height);}
  size();addEventListener('resize',size,{passive:true});

  // ---- scroll / velocity ----
  let p=0,tp=0,pPrev=0,sv=0;
  function read(){const tot=wrap.offsetHeight-innerHeight;const r=wrap.getBoundingClientRect();tp=tot>0?Math.min(1,Math.max(0,-r.top/tot)):0;}
  addEventListener('scroll',read,{passive:true});addEventListener('resize',read,{passive:true});read();
  const ss=(e0,e1,x)=>{const t=Math.min(1,Math.max(0,(x-e0)/(e1-e0)));return t*t*(3-2*t);};
  const clamp=(x,a,b)=>Math.min(b,Math.max(a,x));

  // ---- ignition ----
  let ignite=0,igniting=false,ignited=false,igStart=0;const IG_MS=reduced?300:800;
  function beginIgnite(){if(igniting||ignited)return;igniting=true;igStart=performance.now();}
  if(window.__kottLoaderDone){beginIgnite();}
  else{document.addEventListener('kott:loader:done',beginIgnite,{once:true});if(window.Shopify&&window.Shopify.designMode)setTimeout(beginIgnite,300);setTimeout(beginIgnite,6200);}

  // ---- pointer / custom cursor ----
  const mouse={x:.5,y:.5,tx:.5,ty:.5};
  const cur={x:innerWidth/2,y:innerHeight/2,tx:innerWidth/2,ty:innerHeight/2};
  let hot=0,hotT=0;
  if(!coarse){
    addEventListener('pointermove',e=>{mouse.tx=e.clientX/innerWidth;mouse.ty=1-e.clientY/innerHeight;cur.tx=e.clientX;cur.ty=e.clientY;},{passive:true});
    const stage=root.querySelector('.stage');
    stage.addEventListener('pointerenter',()=>{hotT=1;root.classList.add('is-hot');},{passive:true});
    stage.addEventListener('pointerleave',()=>{hotT=0;root.classList.remove('is-hot');},{passive:true});
  }

  // ---- ticker jump ----
  function jumpTo(i){const tot=wrap.offsetHeight-innerHeight;if(tot<=0)return;const targetP=DEN?i/DEN:0;const top=wrap.getBoundingClientRect().top+scrollY+targetP*tot;scrollTo({top,behavior:reduced?'auto':'smooth'});}
  tks.forEach((b,i)=>b.addEventListener('click',()=>jumpTo(i)));
  const bars=tks.map(b=>b.querySelector('.bar'));

  // ---- hex → rgb[0..1] ----
  function hexRGB(hex){hex=(hex||'').replace('#','');if(hex.length===3)hex=hex.split('').map(c=>c+c).join('');const n=parseInt(hex||'ff6f3c',16);return[((n>>16)&255)/255,((n>>8)&255)/255,(n&255)/255];}

  let curAI=-1,lastPast=false,raf=0,tStart=performance.now();

  function frame(now){
    raf=requestAnimationFrame(frame);
    try{
    pPrev=p;
    p+=(tp-p)*(reduced?1:0.075);
    const vel=p-pPrev;
    sv+=((reduced?0:vel)-sv)*0.12;
    mouse.x+=(mouse.tx-mouse.x)*0.08;mouse.y+=(mouse.ty-mouse.y)*0.08;
    hot+=((reduced?0:hotT)-hot)*0.12;
    const tt=reduced?0:(now-tStart)/1000;

    if(igniting&&!ignited){const e=Math.min(1,(now-igStart)/IG_MS);ignite=1-Math.pow(1-e,3);if(e>=1){ignited=true;igniting=false;ignite=1;}}
    rs.setProperty('--reveal',ignite.toFixed(3));

    // morph position
    const f=p*DEN; let base=Math.floor(f); if(base>=N-1)base=N-1; const next=Math.min(base+1,N-1);
    const local=clamp(f-base,0,1);
    const eased=ss(0.30,0.70,local);
    const ai=clamp(Math.round(f),0,N-1);

    // kinetic titles — tight, offset cross-dissolve so they never overlap as
    // readable mush: outgoing fades+rises out by ~0.42, incoming enters from ~0.58.
    titles.forEach((el,i)=>{
      let op=0,ty=0;
      if(i===base){op=1-ss(0.0,0.42,local);ty=-ss(0.0,0.62,local)*42;}
      else if(i===next&&next!==base){op=ss(0.58,1.0,local);ty=(1-ss(0.4,1.0,local))*42;}
      el.style.opacity=op.toFixed(3);
      el.style.transform='translateY('+ty.toFixed(1)+'px)';
    });

    // active changes
    if(ai!==curAI){
      curAI=ai;
      const acc=data[ai].accent;
      rs.setProperty('--accent',acc);
      tks.forEach((b,i)=>b.classList.toggle('is-active',i===ai));
      if(countNow)countNow.textContent=TWO(ai+1);
      if(hit)hit.setAttribute('href',data[ai].link);
      // Teach the mechanic: while there are more projects, the cue says "scroll
      // for the next project"; on the last one it flips to the view CTA and the
      // chevrons fade (nothing more to scroll to).
      if(hintPill){
        if(ai<N-1){hintPill.textContent=nextLabel;if(hintChevs)hintChevs.style.opacity='';}
        else{hintPill.textContent=viewLabel;if(hintChevs)hintChevs.style.opacity='0';}
      }
    }
    // ticker bar = within-segment progress for the active tile
    if(bars[ai]){bars[ai].style.width=(local*100).toFixed(1)+'%';}
    bars.forEach((bar,i)=>{if(i!==ai&&bar)bar.style.width='0%';});

    // progress + hint
    rs.setProperty('--p',(p*100).toFixed(1)+'%');
    // keep the scroll cue visible through the reel (it persistently signals
    // "keep scrolling to reveal each project") — fade only at the very end.
    rs.setProperty('--hintA',(1-ss(0.93,0.995,p)).toFixed(3));
    const past=p>0.992;if(past!==lastPast&&progEl){progEl.classList.toggle('is-past',past);lastPast=past;}

    // custom cursor
    if(cursor&&!coarse){cur.x+=(cur.tx-cur.x)*0.2;cur.y+=(cur.ty-cur.y)*0.2;
      rs.setProperty('--cx',cur.x.toFixed(1)+'px');rs.setProperty('--cy',cur.y.toFixed(1)+'px');}

    if(glOK){
      const t0=texes[base]||texes.find(Boolean), t1=texes[next]||t0;
      if(t0){
        gl.activeTexture(gl.TEXTURE0);gl.bindTexture(gl.TEXTURE_2D,t0);
        gl.activeTexture(gl.TEXTURE1);gl.bindTexture(gl.TEXTURE_2D,t1||t0);
        const r0=texRes[base]||[2,2], r1=texRes[next]||r0;
        gl.uniform2f(uR0,r0[0],r0[1]);gl.uniform2f(uR1,r1[0],r1[1]);
        gl.uniform2f(uR,cv.width,cv.height);gl.uniform1f(uT,tt);
        gl.uniform2f(uM,mouse.x,mouse.y);gl.uniform1f(uP,eased);
        gl.uniform1f(uV,clamp(sv*34,-1,1));gl.uniform1f(uH,clamp(hot,0,1));
        gl.drawArrays(gl.TRIANGLES,0,3);
      }
    } else {
      // CSS cross-fade fallback
      fbLayers.forEach((el,i)=>{el.style.opacity=(i===base?(1-eased):(i===next?eased:0)).toFixed(3);});
    }
    }catch(e){ if(!window.__prErr){window.__prErr=1;console.error('[proj-fluid]',e);} }
  }
  // Static state for the accessibility "stop animations" toggle: stop the
  // WebGL morph and show the first project as a still frame (its image via the
  // CSS fallback layer + its title + a working link). The CSS twin
  // (html[data-reduce-motion]) collapses the scroll pin and hides the cursor.
  function showStatic(){
    if(raf){cancelAnimationFrame(raf);raf=0;}
    root.classList.add('is-static');               // CSS hides the reel chrome, shows the list
    if(root.querySelector('.proj-static')) return; // build the accessible list once
    var ctaEl=root.querySelector('.t__cta');
    var ctaLabel=ctaEl&&ctaEl.textContent?ctaEl.textContent.trim():'';
    var list=document.createElement('div');
    list.className='proj-static';
    titles.forEach(function(t,i){
      var d=data[i];
      var nameEl=t.querySelector('.t__name'), descEl=t.querySelector('.t__desc'), tagsEl=t.querySelector('.t__tags');
      var card=document.createElement('a');
      card.className='proj-static__card';
      card.href=d.link||'#';
      card.style.setProperty('--accent', d.accent||'#FF6F3C');
      card.innerHTML=
        '<span class="proj-static__media"'+(d.img?(' style="background-image:url(&quot;'+d.img+'&quot;)"'):'')+' aria-hidden="true"></span>'+
        '<span class="proj-static__body">'+
          '<span class="proj-static__idx">'+TWO(i+1)+'</span>'+
          (tagsEl?'<span class="proj-static__tags">'+tagsEl.innerHTML+'</span>':'')+
          '<span class="proj-static__name">'+(nameEl?nameEl.textContent:'')+'</span>'+
          (descEl?'<span class="proj-static__desc">'+descEl.textContent+'</span>':'')+
          '<span class="proj-static__cta">'+ctaLabel+'</span>'+
        '</span>';
      list.appendChild(card);
    });
    (root.querySelector('.wrap')||root).appendChild(list);
  }

  let io=null;
  function startLoop(){
    if(!raf){tStart=performance.now();raf=requestAnimationFrame(frame);}
    if(!io && 'IntersectionObserver' in window){
      io=new IntersectionObserver(ents=>{ents.forEach(en=>{
        if(en.isIntersecting){if(progEl){progEl.classList.remove('is-past');lastPast=false;}if(!raf){tStart=performance.now();raf=requestAnimationFrame(frame);}}
        else{if(raf){cancelAnimationFrame(raf);raf=0;}if(progEl)progEl.classList.add('is-past');hotT=0;root.classList.remove('is-hot');}
      });},{rootMargin:'0px'});
      io.observe(wrap);
    }
  }

  function onReduceChange(e){
    if(e.detail && e.detail.reduced){ if(io){io.disconnect();io=null;} showStatic(); }
    else location.reload();
  }
  if(window.__prReduceHandler) window.removeEventListener('kott:reduce-motion-change', window.__prReduceHandler);
  window.__prReduceHandler=onReduceChange;
  window.addEventListener('kott:reduce-motion-change', onReduceChange);

  if(reduced) showStatic();
  else startLoop();
})();
